<script lang="ts" setup>
import { render, type VNode } from "vue";
import morphdom from "morphdom";

// Constants
const parser = new DOMParser();

// Slots
const slots = defineSlots<{
    default?: any;
    overlay?: any;
    head?: any;
    body?: any;
}>();

// Props;
defineProps<{
    noScroll?: boolean;
}>();
const doc = defineModel<string>("document");

// Refs
const frame = ref<HTMLIFrameElement>();

const head = computed(() => getSlot("head"));
const body = computed(() => getSlot("body"));
const overlay = computed(() => getSlot("overlay", true));

// Functions
const getHead = () => frame.value?.contentDocument?.head as HTMLHeadElement;
const getBody = () => frame.value?.contentDocument?.body as HTMLBodyElement;
const getSlot = (name: keyof typeof slots, orDefault: boolean = false) => {
    const slot = slots[name]
        ? slots[name]
        : orDefault
            ? slots.default
            : undefined;

    return slot ? slot() : undefined;
};

function renderDOM() {
    if (!doc.value) {
        return;
    }

    const dom = parser.parseFromString(doc.value, "text/html");
    morphdom(getHead(), (dom as Document).head.cloneNode(true));
    morphdom(getBody(), (dom as Document).body.cloneNode(true));
}

function renderSlots() {
    getSlot("head")?.forEach((vnode: VNode) => render(vnode, getHead()));
    body.value?.forEach((vnode: VNode) => render(vnode, getBody()));
}

function renderOverlay() {
    if (!overlay.value) {
        return;
    }

    const container = document.createElement("div");
    container.classList.add("web-frame-overlay");
    overlay.value.forEach((vnode: VNode) => render(vnode, container));
    getBody().append(container);
}

function clearOverlay() {
    const overlay = getBody().querySelector(".web-frame-overlay");

    if (overlay) {
        overlay.remove();
    }
}

function renderFrame() {
    // Check if the frame is complete
    if (!frame.value || !getHead() || !getBody()) {
        return;
    }

    // Remove the overlay in case it's present
    clearOverlay();

    // Render the document if a dom could be resolved
    renderDOM();

    // Render any document slots
    renderSlots();

    // Render the overlay
    renderOverlay();
}

// Watchers
watch([doc, body, head, overlay, frame], () => {
    renderFrame();
});

// Events
onMounted(() => {
    renderFrame();
    resume();
});

const { pause, resume, isActive } = useIntervalFn(() => {
    if(getBody()?.childElementCount === 0) {
        renderFrame();
    }
}, 50, {
    immediate: false
})
</script>

<template>
    <iframe ref="frame" :scrolling="noScroll ? 'no' : undefined" />
</template>
